<script>
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { log } from "@/utils/clientLogger.js";

export default defineComponent({
  name: "ClientListItem",
  props: {
    client: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const userRole = computed(() => {
      const role = store.getters.userData?.role;
      return typeof role === 'object' ? role?.name : (role || '');
    });

    const isPending = computed(() => props.client.accessStatus === 'pending');

    return { t, userRole, isPending };
  },
  methods: {
    deleteClient() {
      if (this.client?.orderCount > 0) {
        this.$swal.fire({
          title: this.t('alerts.error'),
          icon: 'error',
          text: this.t('messages.clientHasOrders'),
        });
        return;
      }

      this.$swal.fire({
        title: this.t('alerts.warning'),
        icon: 'warning',
        text: `${this.t('messages.deleteClientMsg')} ${this.client?.name}?`,
        showCancelButton: true,
        confirmButtonText: this.t('buttons.confirm'),
        cancelButtonText: this.t('buttons.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          this.$http.delete("/api/clients/" + this.client?._id)
              .then(() => {
                this.$swal.fire({
                  title: this.t('alerts.success'),
                  icon: "success",
                  text: this.t('messages.successfullyDeleteClientMsg'),
                });
              })
              .catch((e) => {
                log.error('ClientListItem:deleteClient', {
                  error: e,
                  clientId: this.client?._id
                });

                this.$swal.fire({
                  title: this.t('alerts.error'),
                  text: e.response?.data?.message || this.t('messages.successfullyDeleteClientMsg'),
                  icon: "error"
                });
              });
        }
      });
    }
  }
});
</script>

<template>
  <li class="list-group-item d-flex justify-content-between align-items-center" :class="{'bg-light': isPending}">
    <div class="ms-4 me-auto">
      <span
          v-if="!isPending"
          :data-bs-client="client?._id"
          data-bs-target="#editClientModal"
          data-bs-toggle="modal"
          class="fw-bold"
          style="cursor: pointer;">
        {{ client?.name }}
      </span>
      <span v-else class="fw-bold text-muted">
        {{ client?.name }}
      </span>

      <span v-if="userRole === 'admin' && client?.ownerId?.username" class="badge bg-success ms-2" style="font-size: 0.75em;">
         {{ t('labels.clientOwner') }}: {{ client.ownerId.username }}
      </span>

      <span v-if="isPending" class="badge bg-warning text-dark ms-2" style="font-size: 0.75em;">
         <i class="bi bi-hourglass-split"></i> {{ t('labels.waitingConfirmation') }}
      </span>

      <span v-if="!isPending && client?.notes?.length"
            :data-bs-client="client?._id"
            data-bs-target="#editClientModal"
            data-bs-toggle="modal"
            class="badge bg-warning text-dark ms-2 align-middle d-inline-flex align-items-center gap-1"
            style="font-size: 0.75em; cursor: pointer; vertical-align: middle;"
            :title="t('labels.clientNotes', 'Примітки клієнта')">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M12 9h.01" />
          <path d="M11 12h1v4h1" />
          <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z" />
        </svg>
        {{ t('labels.clientNotes', 'Примітки клієнта') }}: {{ client.notes.length }}
      </span>

      <br>
      {{ t('labels.phone') }}: {{ client?.phone }}
    </div>

    <div class="client_btns ms-auto ps-3" v-if="!isPending">
      <button :data-bs-client="client?._id"
              data-bs-target="#editClientModal"
              data-bs-toggle="modal"
              class="btn btn-outline-info me-3"
              style="cursor: pointer;">
        {{ t('buttons.edit') }}
      </button>
      <button
          :data-bs-client="client?._id"
          data-bs-target="#clientOrdersModal"
          data-bs-toggle="modal"
          class="btn btn-outline-warning">
        {{ t('labels.orders') }} - <strong>{{ client?.orderCount }}</strong>
      </button>
      <button
          v-if="userRole === 'admin'"
          class="btn btn-outline-danger ms-2 me-2"
          @click="deleteClient">
        {{ t('buttons.deleteClient') }}
      </button>
    </div>
  </li>
</template>

<style scoped lang="scss">
strong {
  color: var(--bs-dark);
}
.bg-light {
  background-color: #f8f9fa !important;
  opacity: 0.8;
}
</style>